/**
 * ตัวแดง / ตัวเหลือง on the body (lane DUEL-ARMOR; rules packages/shared systems/duelArmor.ts). Pixel rules: scene grid,
 * master ramp (fx/pixelRules.ts FX_RAMPS), 1-px outline, stepped, no glow.
 * - The rim (render/figureRims.ts): red = a steady stippled rim (every 2nd pixel, dark between; shape keeps it apart
 *   from the red break brackets, which are solid corners; docs/design/ui/2026-10-11-armour-tell); yellow = a steady ochre rim. Dark ring outside.
 * - A 7x7 plate over the head (colour-blind read): red a full plate, yellow a half plate, 1-px dark outline.
 * - A blow soaked: a small grey clank cross at the chest (0.15 s); armour broken: five pixel shards fall off (0.2 s).
 * Also feeds the knock views to render/knockReact.ts once a frame.
 */
import { duelArmorOf, duelKnockOf, KARMA_LABELS, type Entity, type World } from '@nine/shared';
import { FX_RAMPS } from '../fx/pixelRules';
import { registerFigureRim } from '../figureRims';
import { setKnockView } from '../knockReact';
import { UI } from '../labels';
import { globeBend } from '../globe';
import { registerRenderPass } from './registry';

const MAROON = FX_RAMPS.maroon!;
const GOLD = FX_RAMPS.gold!;
/** Yellow reads on pale robes only as a saturated ochre (the pale gold vanished on the starter's white, GIF r1). */
const OCHRE = FX_RAMPS.ochre!;
const STEEL = FX_RAMPS.steel!;
const INK = '#0e0a10';

registerFigureRim((w, e) => {
  if (!w.pvpBout?.fight) return null;
  const a = duelArmorOf(w)?.view(e.id);
  if (!a) return null;
  if (a.tier === 'red')
    return { rim: MAROON[4]!, outer: MAROON[0]!, stipple: true }; // steady + stippled: not the break brackets (UX 10-11)
  return { rim: OCHRE[3]!, outer: OCHRE[0]! };
});

/** Plate pixels (7x7): 1 = fill, 2 = dark line. */
const PLATE = ['2222222', '2111112', '2111112', '2111112', '2111112', '0211120', '0022200'];

registerRenderPass({
  id: 'duelArmorTell',
  layer: 'labels',
  order: 109,
  draw({ mg, world: w, px: PX, iso, ox, oy }) {
    const b = w.pvpBout;
    const ids = b ? [b.a, b.b] : [];
    const knock = duelKnockOf(w);
    for (const id of ids) setKnockView(id, b?.fight ? (knock?.view(id) ?? null) : null);
    if (!b?.fight) return;
    const armor = duelArmorOf(w);
    if (!armor) return;
    for (const id of ids) {
      const e = w.get(id);
      if (!e || e.dead) continue;
      const a = armor.view(id);
      const soaked = w.time - (armor.soakedAt.get(id) ?? -9);
      const broke = w.time - (armor.brokeAt.get(id) ?? -9);
      if (!a && soaked > 0.15 && broke > 0.2) continue;
      const [sx, sy] = iso.proj(e.x, e.y);
      const cx = Math.round((sx + ox) * PX);
      const head = Math.round(globeBend(sx + ox, sy + oy - 54 * e.scale)[1] * PX);
      const chest = Math.round(globeBend(sx + ox, sy + oy - 26 * e.scale)[1] * PX);
      const p = Math.max(2, Math.round(PX));
      // The plate now sits in the name row (armourBadge, called from personLabels); only a non-person keeps it overhead.
      if (a && e.kind !== 'player' && e.kind !== 'rival')
        plate(mg, cx - Math.floor(3.5 * p), head - 7 * p, p, a.tier === 'red' ? MAROON[4]! : OCHRE[3]!, a.tier === 'yellow');
      if (soaked <= 0.15) clank(mg, cx, chest, p);
      if (broke <= 0.2) shards(mg, cx, chest, p, broke, e);
    }
  },
});

function plate(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  p: number,
  col: string,
  half: boolean,
): void {
  for (let r = 0; r < 7; r++)
    for (let c = 0; c < 7; c++) {
      const v = PLATE[r]![c];
      if (v === '0') continue;
      // Half plate: the right half is left dark (hollow).
      g.fillStyle = v === '2' ? INK : half && c >= 4 ? INK : col;
      g.fillRect(x + c * p, y + r * p, p, p);
    }
}

function clank(g: CanvasRenderingContext2D, x: number, y: number, p: number): void {
  g.fillStyle = INK;
  g.fillRect(x - 2 * p, y - p, 5 * p, 3 * p);
  g.fillRect(x - p, y - 2 * p, 3 * p, 5 * p);
  g.fillStyle = STEEL[5]!;
  g.fillRect(x - p, y, 3 * p, p);
  g.fillRect(x, y - p, p, 3 * p);
}

function shards(g: CanvasRenderingContext2D, x: number, y: number, p: number, t: number, e: Entity): void {
  // Stepped fall: 12 fps.
  const k = Math.floor(t * 12) / 12;
  for (let i = 0; i < 5; i++) {
    const dx = ((i * 7 + e.id) % 9) - 4;
    const fall = Math.round(k * (40 + i * 8));
    g.fillStyle = INK;
    g.fillRect(x + dx * 2 * p - p, y + (fall - 10) * (p / 2) - p, 3 * p, 3 * p);
    g.fillStyle = GOLD[5]!;
    g.fillRect(x + dx * 2 * p, y + (fall - 10) * (p / 2), p, p);
  }
}

/**
 * The armour plate in the name row, left of the karma badge (3 screen px per plate pixel; called from personLabels).
 * x = name centre, y = name baseline row as passed to karmaBadge, nameW = measured name width.
 */
export function armourBadge(g: CanvasRenderingContext2D, w: World, e: Entity, x: number, y: number, nameW: number): void {
  if (!w.pvpBout?.fight) return;
  const a = duelArmorOf(w)?.view(e.id);
  if (!a) return;
  g.save();
  g.font = `600 11px ${UI}`;
  const sc = 3;
  // Left of karmaBadge (render/labels.ts): its left edge = x - nameW/2 - (labelW + 10) - 6.
  const bx = Math.round(x - nameW / 2 - g.measureText(KARMA_LABELS[e.karma]).width - 10 - 6 - 4 - 7 * sc);
  const by = Math.round(y - 13);
  g.restore();
  plate(g, bx, by, sc, a.tier === 'red' ? MAROON[4]! : OCHRE[3]!, a.tier === 'yellow');
}
